import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Footer from './Footer'
import { ChevronLeftIcon } from './ui'
import { PrimaryButton } from './ui'
import { isDevMode } from '../utils/devMode'
import { usePortalAuth } from '../auth/authContexts'
import { isAuth0EmailVerificationRequiredFromUnknown, captureEmailVerificationRequiredFromCallback } from '../utils/auth0EmailVerification'
import { useEmailVerificationRequiredModal } from '../hooks/useEmailVerificationRequiredModal'
import EmailVerificationRequiredModal from './EmailVerificationRequiredModal'
import PostLoginLoadingScreen from './PostLoginLoadingScreen'
import { marketingAuthGatewayPath } from '../utils/marketingAuthEntry'
import { CLUB4X4_SELF_SERVICE_HREF } from '../claims/contactLinks'

export default function Register() {
  const { isAuthenticated, isLoading, loginWithRedirect, authError } = usePortalAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { open: emailVerificationModalOpen, dismiss: dismissEmailVerificationModal, show: showEmailVerificationModal } =
    useEmailVerificationRequiredModal()
  const marketingRedirectAttempted = useRef(false)
  const [marketingRedirectInFlight, setMarketingRedirectInFlight] = useState(false)

  const isMarketingSignupEntry =
    !isDevMode() && searchParams.get('signup') === '1' && !isAuthenticated

  const handleSignUp = async () => {
    if (isDevMode()) {
      console.log('🔧 DEV MODE: Skipping auth, redirecting to dashboard')
      navigate('/dashboard')
      return
    }
    try {
      await loginWithRedirect({ signup: true })
    } catch (error) {
      if (isAuth0EmailVerificationRequiredFromUnknown(error)) {
        showEmailVerificationModal()
      }
      console.error('Error during sign up:', error)
    }
  }

  useEffect(() => {
    if (captureEmailVerificationRequiredFromCallback(authError)) {
      showEmailVerificationModal()
    }
  }, [authError, showEmailVerificationModal])

  useLayoutEffect(() => {
    if (!isMarketingSignupEntry || marketingRedirectAttempted.current) {
      return
    }
    setMarketingRedirectInFlight(true)
  }, [isMarketingSignupEntry])

  useEffect(() => {
    if (isDevMode()) {
      return
    }
    if (isAuthenticated) {
      sessionStorage.setItem('club4x4_visited_confirm_details', 'true')
      navigate('/dashboard', { replace: true })
      return
    }
    const shouldAutoSignUp =
      !isLoading && isMarketingSignupEntry && !marketingRedirectAttempted.current
    if (shouldAutoSignUp) {
      marketingRedirectAttempted.current = true
      setMarketingRedirectInFlight(true)
      void Promise.resolve(loginWithRedirect({ signup: true })).catch((error) => {
        setMarketingRedirectInFlight(false)
        if (isAuth0EmailVerificationRequiredFromUnknown(error)) {
          showEmailVerificationModal()
        }
        console.error('Error during sign up from marketing entry:', error)
        navigate(marketingAuthGatewayPath('/register'), { replace: true })
      })
    }
  }, [
    isAuthenticated,
    isLoading,
    isMarketingSignupEntry,
    loginWithRedirect,
    navigate,
    showEmailVerificationModal,
  ])

  if (marketingRedirectInFlight && !isAuthenticated) {
    return <PostLoginLoadingScreen />
  }

  return (
    <div className="bg-background-brand-primary flex flex-col items-center justify-center min-h-screen w-full">
      <main id="main" className="flex w-full flex-col items-center justify-center">
      <div className="flex flex-col gap-[32px] items-center justify-center px-[16px] py-[80px] relative w-full">
        <div className="flex flex-col gap-[32px] items-start max-w-sm px-[16px] py-0 relative w-full">
          <a
            href={CLUB4X4_SELF_SERVICE_HREF}
            className="cursor-pointer flex gap-[4px] items-center justify-center p-0 relative shrink-0"
          >
            <div className="overflow-clip relative shrink-0 size-5 text-foreground-brand-secondary-alt">
              <ChevronLeftIcon />
            </div>
            <p className="font-body font-semibold text-sm leading-sm text-text-brand-secondary-700">
              Back to Club 4X4
            </p>
          </a>
        </div>

        <div className="flex items-center justify-center px-[16px] py-0 relative shrink-0 w-full">
          <div className="bg-background-primary flex flex-[1_0_0] flex-col items-start max-w-sm overflow-clip relative rounded-xl shadow-lg shrink-0 w-full">
            <div className="bg-foreground-brand-primary-600 flex flex-col items-center justify-center px-[24px] py-[20px] relative w-full">
              <div className="flex items-start relative w-[200px]">
                <div className="aspect-[596/159] flex-1 min-h-px min-w-px overflow-clip relative">
                  <img
                    src="/Clip path group.svg"
                    alt="Club 4X4"
                    className="h-full w-auto object-contain brightness-0 invert"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-[32px] items-start px-[24px] py-[40px] relative w-full">
              <div className="flex flex-col gap-[24px] items-center relative w-full">
                <div className="flex flex-col gap-[8px] items-start text-center w-full">
                  <h1 className="m-0 font-display leading-[32px] relative shrink-0 text-text-primary-900 text-display-xs w-full">
                    Create an account
                  </h1>
                  <p className="font-body font-regular text-md leading-md text-text-tertiary-600 w-full">
                    Sign up for your Club 4X4 account.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-[24px] items-center rounded-lg w-full">
                <PrimaryButton onClick={handleSignUp} fullWidth>
                  Create account
                </PrimaryButton>
              </div>

              <div className="content-baseline flex flex-wrap gap-[4px] items-baseline justify-center relative shrink-0 w-full">
                <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 relative shrink-0">
                  Already have an account?
                </p>
                <Link
                  to="/signin"
                  className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
                >
                  <p className="font-body font-semibold text-sm leading-sm text-text-brand-secondary-700">
                    Sign in
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      </main>

      <EmailVerificationRequiredModal open={emailVerificationModalOpen} onClose={dismissEmailVerificationModal} />

      <Footer />
    </div>
  )
}
